import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Screen } from "../components/Screen";
import { PhotoWithSkeleton } from "../components/PhotoWithSkeleton";
import { PowertrainBadge } from "../components/PowertrainBadge";
import { api } from "../api/client";
import type { ModelDetailResponse, TrimWithColors } from "../api/types";
import { formatUAH } from "../utils/format";
import { useAppStore } from "../state/store";
import { track } from "../analytics/track";

/**
 * п.3 ТЗ: комплектації, кольори, актуальні ціни, автооновлення ціни при зміні вибору,
 * заборона некоректних комбінацій (доступні кольори підвантажуються з бекенду саме для
 * обраної комплектації — інших варіантів просто немає в списку). За замовчуванням —
 * найдешевша актуальна комплектація (defaultTrimId з API).
 */
export function CarDetail() {
  const { slug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSelectedCar = useAppStore((s) => s.setSelectedCar);

  const [data, setData] = useState<ModelDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrimId, setSelectedTrimId] = useState<string | null>(null);
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ModelDetailResponse>(`/api/models/${slug}`)
      .then((res) => {
        setData(res);
        setSelectedTrimId(res.defaultTrimId);
        const defaultTrim = res.trims.find((t) => t.id === res.defaultTrimId);
        setSelectedColorId(defaultTrim?.availableColors[0]?.id ?? null);
      })
      .catch((e) => setError(e.message));
  }, [slug]);

  const selectedTrim: TrimWithColors | undefined = useMemo(
    () => data?.trims.find((t) => t.id === selectedTrimId),
    [data, selectedTrimId]
  );
  const priceUAH = selectedTrim && selectedColorId ? selectedTrim.priceByColor[selectedColorId] ?? null : null;

  function goToCalculate() {
    if (!data || !selectedTrim || !selectedColorId || priceUAH === null) return;
    const color = selectedTrim.availableColors.find((c) => c.id === selectedColorId)!;
    setSelectedCar({
      modelSlug: data.model.slug,
      modelName: data.model.name,
      trimId: selectedTrim.id,
      trimName: selectedTrim.name,
      colorOptionId: color.id,
      colorCode: color.code,
      priceUAH,
      powertrainType: selectedTrim.powertrainType,
    });
    track("click_calculate", { modelSlug: data.model.slug, trimId: selectedTrim.id });
    navigate("/credit");
  }

  // Швидкий шлях "Розрахувати" прямо зі списку каталогу (п.7 ТЗ — не змушувати вводити дані повторно)
  useEffect(() => {
    if (searchParams.get("calculate") === "1" && data && selectedTrim && selectedColorId) {
      goToCalculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data, selectedTrim, selectedColorId]);

  if (error) return <Screen title="Помилка" showBack>{error}</Screen>;
  if (!data) return <Screen title="Завантаження…" showBack><div className="loading-row">Завантаження…</div></Screen>;

  return (
    <Screen title={data.model.name} showBack>
      <PhotoWithSkeleton src={data.model.photoUrl} alt={data.model.name} />

      {/*
        Код комплектації (SFX, напр. "ET") у прайсі — це рівень оснащення, а НЕ унікальний
        ідентифікатор: той самий код повторюється для різних двигунів/приводів (напр. "ET" є
        і у 1.8L, і в 2.0L варіанта C-HR). Тому в списку показуємо повний опис (двигун + рівень
        оснащення) замість самого лише коду — інакше два різні автомобілі виглядали б як
        однакові чипи без можливості їх розрізнити.
      */}
      <div className="section-title">Комплектація</div>
      <div className="card-list" style={{ marginBottom: 12 }}>
        {data.trims.map((trim) => {
          const trimPrices = Object.values(trim.priceByColor).filter((p): p is number => p !== null);
          const minPrice = trimPrices.length ? Math.min(...trimPrices) : null;
          return (
            <button
              key={trim.id}
              className="card"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                textAlign: "left",
                border: trim.id === selectedTrimId ? "2px solid var(--color-primary)" : "2px solid transparent",
                background: "var(--color-surface)",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedTrimId(trim.id);
                setSelectedColorId(trim.availableColors[0]?.id ?? null);
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>{trim.engineDescription}</span>
              {minPrice !== null && (
                <span style={{ fontSize: 13, color: "var(--color-text-secondary)", whiteSpace: "nowrap", marginLeft: 8 }}>
                  {formatUAH(minPrice)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedTrim && (
        <>
          <div style={{ marginTop: 6 }}>
            <PowertrainBadge type={selectedTrim.powertrainType} />
          </div>

          <div className="section-title">Колір</div>
          <div className="chip-row">
            {selectedTrim.availableColors.map((color) => (
              <button
                key={color.id}
                className={`color-swatch ${color.id === selectedColorId ? "active" : ""}`}
                style={color.hex ? { background: color.hex } : undefined}
                title={color.code}
                onClick={() => setSelectedColorId(color.id)}
              >
                {!color.hex && color.code.slice(0, 2)}
              </button>
            ))}
          </div>

          <div className="card" style={{ padding: 16, marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Ціна</div>
              <div className="price" style={{ fontSize: 22 }}>
                {priceUAH !== null ? formatUAH(priceUAH) : "Оберіть колір"}
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: "auto" }} disabled={priceUAH === null} onClick={goToCalculate}>
              Розрахувати
            </button>
          </div>
        </>
      )}
    </Screen>
  );
}
